import React, { useEffect, useState } from 'react';
import {
  TextField,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  FormControlLabel,
  Checkbox,
  Grid,
  Paper,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  PostBlockList,
  PutTypeMaster,
  TypeMaster,
} from '../../../../services/apiTypes';
import useErrorModal from '../../../../hooks/useErrorModal';
import { API_CODE } from '../../../../_constants/apiConst';
import { MaxLength } from '../../../../_constants/maxLengthConst';
import UploadTypeFile from './UploadTypeFiles/UploadTypeFile';
import DisplayNgSample from './UploadTypeFiles/DisplayNgSample';
import {
  CHUNK_SIZE,
  MANUAL_CONTAINER_NAME,
  NG_SAMPLE_CONTAINER_NAME,
} from '../../../../_constants/inspectionConst';
import { putTypeMaster } from '../../../../services/api/typeMaster';
import {
  deleteFiles,
  uploadBlockData,
  uploadBlockList,
} from '../../../../stores/confirmedInspection/api';
import DisplayManual from './UploadTypeFiles/DisplayManual';
import { LOADING_BACKDROP_STYLE } from '../../../../_constants/styleConst';
import {
  ACCEPT_MAX_NG_SAMPLE,
  ACCEPT_SIZE_NG_SAMPLE,
  ACCEPT_TYPE_MANUAL,
  ACCEPT_TYPE_NG_SAMPLE,
  NEW_IMAGE_START_NO,
  VALID_MANUAL_PAGE_RANGE,
} from '../../../../_constants/manageConst';
import { getBlobData } from '../../../../services/api/BlobData';
import { isGetBlobData } from '../../../../_utils/typeGuardUtils';
import ErrorModal from '../../../../_common/Modal/ErrorModal';

export type NgSampleData = {
  imageNo: number;
  labelName: string;
  labelNameJp: string;
  labelName03: string;
  labelName04: string;
  labelName05: string;
  labelName06: string;
  labelName07: string;
  labelName08: string;
  labelName09: string;
  labelName10: string;
  imagePath: string;
  sort: number;
  /** 画面表示用のプロパティ */
  file: File | null;
  fileName: string;
  error: string;
  isChanged: boolean;
  isDeleted: boolean;
};

export type ManualInfo = {
  /** ページ情報を除いたパス */
  path: string;
  /** 初期表示ページ */
  page: string;
  /** アップロードするファイル */
  file: File | null;
  /** 表示するファイル名 */
  fileName: string;
  /** 変更フラグ */
  isChanged: boolean;
};

const defaultType: PutTypeMaster = {
  categoryId: '',
  typeId: '',
  typeName: '',
  typeNameJp: '',
  typeName03: '',
  typeName04: '',
  typeName05: '',
  typeName06: '',
  typeName07: '',
  typeName08: '',
  typeName09: '',
  typeName10: '',
  sort: 0,
  isOptionalAddition: false,
  isVisualInspection: false,
  documentPath: '',
  memo: '',
  typeImageMaster: [],
};

const defaultNgSample = {
  imageNo: 0,
  labelName: '',
  labelNameJp: '',
  labelName03: '',
  labelName04: '',
  labelName05: '',
  labelName06: '',
  labelName07: '',
  labelName08: '',
  labelName09: '',
  labelName10: '',
  imagePath: '',
  sort: 0,
  file: {},
  fileName: '',
  error: '',
  isChanged: false,
  isDeleted: false,
};

const defaultManualInfo = {
  path: '',
  page: '',
  file: null,
  fileName: '',
  isChanged: false,
};

type EditTypeModalProps = {
  type: TypeMaster;
  isOpen: boolean;
  isAdmin: boolean;
  handleSave: () => void;
  handleCancel: () => void;
  getPath: (categoryId: string, typeId: string, fileName: string) => string;
};

const EditTypeModal: React.FC<EditTypeModalProps> = ({
  type,
  isOpen,
  isAdmin,
  handleSave,
  handleCancel,
  getPath,
}) => {
  const { t } = useTranslation();
  const {
    error,
    showClientError,
    hideError,
    getApiErrorMessage,
    getTypeErrorMessage,
  } = useErrorModal();
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [editType, setEditType] = useState<PutTypeMaster>(defaultType);
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabledSaveBtn, setIsDisabledSaveBtn] = useState(false);
  /** NGサンプル情報 */
  const [ngSamples, setNgSamples] = useState<NgSampleData[]>([]);
  /** マニュアル情報 */
  const [manualInfo, setManualInfo] = useState<ManualInfo>(defaultManualInfo);
  /** 新規追加のimageNo */
  const [newImageNo, setNewImageNo] = useState(NEW_IMAGE_START_NO);

  useEffect(() => {
    if (isOpen && type.typeId) {
      setIsDisabledSaveBtn(true);
      const { displayName, displayType, ...putType } = type;
      setEditType(putType);
      // マニュアル
      const hashIndex = type.documentPath.lastIndexOf('#page=');
      let path = '';
      let page = '';
      if (hashIndex === -1) {
        path = type.documentPath;
      } else {
        path = type.documentPath.substring(0, hashIndex);
        page = type.documentPath.substring(hashIndex + 6);
      }
      const fileName = getFileNameFromPath(path);
      setManualInfo({
        path,
        page,
        file: null,
        fileName,
        isChanged: false,
      });
      // NGサンプル
      const imageMaster: NgSampleData[] = type.typeImageMaster
        .sort((a, b) => a.sort - b.sort)
        .map((data) => ({
          ...data,
          file: null,
          fileName: getFileNameFromPath(data.imagePath),
          error: '',
          isChanged: false,
          isDeleted: false,
        }));
      setNgSamples(imageMaster);
      getNgSampleImageData();
    }
  }, [isOpen]);

  // NGサンプルのラベル(英語)が未入力の場合は保存ボタン非活性
  useEffect(() => {
    if (
      isLoading ||
      !ngSamples.every((data) => (data.file ? true : data.error))
    ) {
      // ローディング中は保存中なので入力チェック不要
      return;
    }
    setIsDisabledSaveBtn(false);
    if (ngSamples.length > 0) {
      let isDisabled = false;
      ngSamples.map((ngSample) => {
        if (!isDisabled && ngSample.labelName.trim() == '') {
          setIsDisabledSaveBtn(true);
          isDisabled = true;
        }
      });
    }
  }, [ngSamples]);

  /**  NGサンプルのデータ取得 */
  const getNgSampleImageData = async () => {
    if (type.typeImageMaster.length > 0) {
      const dataList: NgSampleData[] = [];
      return await Promise.all(
        type.typeImageMaster.map(async (ngSample) => {
          let ngSampleData: NgSampleData = {
            ...ngSample,
            error: '',
            file: null,
            fileName: getFileNameFromPath(ngSample.imagePath),
            isChanged: false,
            isDeleted: false,
          };
          try {
            const imgResponse = await getBlobData({
              containerName: NG_SAMPLE_CONTAINER_NAME,
              path: ngSample.imagePath,
            });
            if (!isGetBlobData(imgResponse)) {
              ngSampleData = {
                ...ngSampleData,
                error: getTypeErrorMessage(API_CODE.BLOB_DATA),
              };
              dataList.push(ngSampleData);
            } else {
              // fileに変換してngSamplesに持たせる
              ngSampleData = {
                ...ngSampleData,
                file: base64ToFile(imgResponse[0].data, imgResponse[0].path),
              };
              dataList.push(ngSampleData);
            }
          } catch {
            ngSampleData = {
              ...ngSampleData,
              error: t('ERR_DOWNLOADING_IMAGE'),
            };
            dataList.push(ngSampleData);
          } finally {
            setNgSamples((prev) => {
              const newNgSamples = [...prev];
              const index = newNgSamples.findIndex(
                (ng) => ng.imageNo === ngSampleData.imageNo,
              );
              newNgSamples[index] = ngSampleData;
              return newNgSamples;
            });
            // 全件取得完了
            if (dataList.length === ngSamples.length) {
              setIsDisabledSaveBtn(false);
            }
          }
        }),
      );
    } else {
      setIsDisabledSaveBtn(false);
    }
  };

  /** パスからファイル名を取得する */
  const getFileNameFromPath = (path: string): string => {
    return path.substring(path.lastIndexOf('/') + 1);
  };

  /** Base64をFile型にデコードして返却 */
  const base64ToFile = (base64String: string, fileName: string): File => {
    const name = fileName.split('/')[2];
    const parts = base64String.split(';base64,');
    const mimeType = parts[0].split(':')[1];
    const base64Data = parts[1];

    const binaryString = atob(base64Data);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: mimeType });
    return new File([blob], name, { type: mimeType });
  };

  /** NGサンプルを除く中項目の入力処理 */
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    const fieldName = name as keyof PutTypeMaster;
    if (name === 'page') {
      // 数値入力があれば範囲内に丸める
      const newValue =
        Number(value) < VALID_MANUAL_PAGE_RANGE.MIN
          ? VALID_MANUAL_PAGE_RANGE.MIN.toString()
          : Number(value) > VALID_MANUAL_PAGE_RANGE.MAX
            ? VALID_MANUAL_PAGE_RANGE.MAX.toString()
            : value;
      setManualInfo({ ...manualInfo, page: value ? newValue : '' });
    } else {
      setEditType({ ...editType, [fieldName]: value });
    }
  };

  /** マニュアルの追加・変更 */
  const handleManualUpload = (file: File | null) => {
    if (!file) {
      // 削除
      setManualInfo({ ...defaultManualInfo, isChanged: true });
      return;
    }
    if (!ACCEPT_TYPE_MANUAL.split(', ').includes(file.type)) {
      // PDF以外のファイルは受け付けない
      return;
    }
    setManualInfo({
      ...manualInfo,
      file,
      fileName: file.name,
      isChanged: true,
    });
  };

  /** NGサンプル削除 */
  const deleteNgSample = (imageNo: number) => {
    if (NEW_IMAGE_START_NO < imageNo) {
      // 新規の場合は配列から削除する
      setNgSamples(ngSamples.filter((ng) => ng.imageNo !== imageNo));
      return;
    }
    // 既存の場合は削除フラグを立てる
    setNgSamples((prev) => {
      const newNgSamples = [...prev];
      const index = newNgSamples.findIndex((ng) => ng.imageNo === imageNo);
      newNgSamples[index] = {
        ...newNgSamples[index],
        isChanged: true,
        isDeleted: true,
      };
      return newNgSamples;
    });
  };

  /** NGサンプルラベル名編集 */
  const handleNgLabelInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    imageNo: number,
  ) => {
    const { name, value } = e.target;
    const fieldName = name as keyof TypeMaster;
    setNgSamples((prev) => {
      const newNgSamples = [...prev];
      const index = newNgSamples.findIndex((ng) => ng.imageNo === imageNo);
      newNgSamples[index] = { ...newNgSamples[index], [fieldName]: value };
      return newNgSamples;
    });
  };

  /** 画像の変更 */
  const handleNgImageChange = (file: File, imageNo: number) => {
    if (
      file.size > ACCEPT_SIZE_NG_SAMPLE ||
      !ACCEPT_TYPE_NG_SAMPLE.split(', ').includes(file.type)
    ) {
      // ファイルサイズが上限を超えている または拡張子が違う場合
      return;
    }
    if (ngSamples.some(({ fileName }) => fileName === file.name)) {
      showClientError(t('ERR_FILE_NAME_EXIST'));
      return;
    }
    setNgSamples((prev) => {
      const newNgSamples = [...prev];
      const index = newNgSamples.findIndex((ng) => ng.imageNo === imageNo);
      newNgSamples[index] = {
        ...newNgSamples[index],
        file,
        fileName: file.name,
        isChanged: true,
        error: '',
      };
      return newNgSamples;
    });
  };

  /** 画像の追加 */
  const handleNgImageAdd = (file: File) => {
    if (
      file.size > ACCEPT_SIZE_NG_SAMPLE ||
      !ACCEPT_TYPE_NG_SAMPLE.split(', ').includes(file.type)
    ) {
      // ファイルサイズが上限を超えている または拡張子が違う場合
      return;
    }
    if (ngSamples.some(({ fileName }) => fileName === file.name)) {
      showClientError(t('ERR_FILE_NAME_EXIST'));
      return;
    }
    const number =
      ngSamples.length > 0 ? ngSamples[ngSamples.length - 1].sort + 1 : 0;
    const newNgSample = {
      ...defaultNgSample,
      imageNo: newImageNo,
      sort: number,
      file,
      fileName: file.name,
      isChanged: true,
    };
    setNgSamples([...ngSamples, newNgSample]);
    setNewImageNo((prev) => prev + 1);
  };

  /** アップロード共通処理 */
  const uploadFile = async (
    containerName: string,
    file: File,
    path: string,
  ) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const blockIds: string[] = [];

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file?.slice(start, end);
      const blockId = btoa(i.toString().padStart(6, '0'));
      blockIds.push(blockId);

      // ブロックデータの送信
      const formData = new FormData();
      formData.append('containerName', containerName);
      // 編集後のラベルで新規フォルダ作成
      formData.append('filePath', path);
      formData.append('blockId', blockId);
      formData.append('fileSize', chunk.size.toString());
      formData.append('blockData', chunk);

      try {
        await uploadBlockData(formData);
      } catch (error) {
        setErrorMsg(getApiErrorMessage(error, API_CODE.UPLOAD_FILE_STAGE));
        return false;
      }
    }

    // ブロックデータの結合
    const body: PostBlockList = {
      containerName,
      filePath: path,
      blockIdList: blockIds,
    };
    try {
      await uploadBlockList(body);
    } catch (error) {
      setErrorMsg(getApiErrorMessage(error, API_CODE.UPLOAD_FILE_COMMIT));
      return false;
    }
    return true;
  };

  /** マニュアルアップロード */
  const uploadManual = async () => {
    try {
      if (manualInfo.isChanged) {
        // ファイル削除(編集前のラベルで元のフォルダを削除)
        await deleteFiles(
          MANUAL_CONTAINER_NAME,
          getPath(editType.categoryId, editType.typeId, ''),
        );
      }
    } catch (error) {
      setErrorMsg(getApiErrorMessage(error, API_CODE.DELETE_FILES));
      return false;
    }

    // 1ファイルずつファイルデータを分割してアップロードする
    const file = manualInfo.file;
    if (file) {
      // imagePath更新
      const imagePath = getPath(
        editType.categoryId,
        editType.typeId,
        manualInfo.fileName,
      );
      setManualInfo({
        ...manualInfo,
        path: imagePath + manualInfo.page ? '#page=' + manualInfo.page : '',
      });
      return await uploadFile(MANUAL_CONTAINER_NAME, file, imagePath);
    }
    return true;
  };

  /** 画像アップロード */
  const uploadImages = async () => {
    // 1ファイルずつファイルデータを分割してアップロードする
    for (let i = 0; i < ngSamples.length; i++) {
      const file = ngSamples[i].file;
      if (ngSamples[i].isChanged) {
        try {
          const originalPath = type.typeImageMaster.find(
            (data) => data.imageNo === ngSamples[i].imageNo,
          )?.imagePath;

          if (originalPath) {
            const fileName = getFileNameFromPath(originalPath);
            // ファイル削除
            await deleteFiles(
              NG_SAMPLE_CONTAINER_NAME,
              getPath(editType.categoryId, editType.typeId, fileName),
            );
          }
        } catch (error) {
          setErrorMsg(getApiErrorMessage(error, API_CODE.DELETE_FILES));
          return false;
        }

        // 削除ではない変更でファイルがある場合はアップロードする
        if (!ngSamples[i].isDeleted && file) {
          // imagePath更新
          const imagePath = getPath(
            editType.categoryId,
            editType.typeId,
            file.name,
          );
          // アップロード
          const isSuccess = await uploadFile(
            NG_SAMPLE_CONTAINER_NAME,
            file,
            imagePath,
          );
          if (!isSuccess) {
            return false;
          }
        }
      }
    }
    return true;
  };

  /** 保存ボタン押下 */
  const onClickSave = async () => {
    setErrorMsg('');
    try {
      setIsDisabledSaveBtn(true);
      setIsLoading(true);
      // マニュアルのアップロード
      const isSuccessDoc = await uploadManual();
      if (!isSuccessDoc) {
        setIsDisabledSaveBtn(false);
        setIsLoading(false);
        return;
      }
      // 画像のアップロード
      const isSuccessImg = await uploadImages();
      if (!isSuccessImg) {
        setIsDisabledSaveBtn(false);
        setIsLoading(false);
        return;
      }
      // NGサンプルのimagNoとsortを振り直す
      const typeImageMaster = [...ngSamples]
        .filter((data) => !data.isDeleted)
        .map((ng, index) => ({
          ...ng,
          imageNo: index,
          imagePath: getPath(editType.categoryId, editType.typeId, ng.fileName),
          sort: index,
        }));
      // マニュアルのパス取得とページ指定
      let manual = '';
      if (manualInfo.fileName) {
        manual =
          getPath(editType.categoryId, editType.typeId, manualInfo.fileName) +
          (manualInfo.page ? '#page=' + manualInfo.page : '');
      }
      const putType: PutTypeMaster = {
        ...editType,
        documentPath: manual,
        typeImageMaster,
      };
      await putTypeMaster(putType);
      handleSave();
      resetData();
    } catch (error) {
      setErrorMsg(getApiErrorMessage(error, API_CODE.TYPE_MASTER));
      setIsDisabledSaveBtn(false);
      setIsLoading(false);
    }
  };

  /** ダイアログを閉じるときに実行する処理 */
  const closeModal = (isClose: boolean) => {
    if (isClose === true) {
      resetData();
      handleCancel();
    }
  };

  /** 各useStateをリセットする */
  const resetData = () => {
    setEditType(defaultType);
    setManualInfo(defaultManualInfo);
    setNgSamples([]);
    setErrorMsg('');
    setIsLoading(false);
  };

  return (
    <Dialog open={isOpen} onClose={closeModal} fullWidth maxWidth={'md'}>
      <Box display="flex">
        <DialogTitle>{t('LBL_EDIT')}</DialogTitle>
        <Typography alignSelf="center" color={'error'}>
          {errorMsg}
        </Typography>
      </Box>
      <DialogContent>
        <Box>
          <TextField
            autoFocus
            margin="dense"
            label={t('LBL_TYPE_NAME_LANG', { 0: t('LBL_LANG_EN') })}
            type="text"
            fullWidth
            name="typeName"
            value={editType.typeName}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: {
                maxLength: MaxLength.ManageInspectionItem.TYPE_NAME,
              },
            }}
          />
          <TextField
            margin="dense"
            label={t('LBL_TYPE_NAME_LANG', { 0: t('LBL_LANG_JA') })}
            type="text"
            fullWidth
            name="typeNameJp"
            value={editType.typeNameJp}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: {
                maxLength: MaxLength.ManageInspectionItem.TYPE_NAME,
              },
            }}
          />
          <TextField
            margin="dense"
            label={t('LBL_TYPE_NAME_LANG', { 0: t('lang3') })}
            type="text"
            fullWidth
            name="typeName03"
            value={editType.typeName03}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: {
                maxLength: MaxLength.ManageInspectionItem.TYPE_NAME,
              },
            }}
          />
          <TextField
            margin="dense"
            label={t('LBL_TYPE_NAME_LANG', { 0: t('lang4') })}
            type="text"
            fullWidth
            name="typeName04"
            value={editType.typeName04}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: {
                maxLength: MaxLength.ManageInspectionItem.TYPE_NAME,
              },
            }}
          />
          <TextField
            margin="dense"
            label={t('LBL_TYPE_NAME_LANG', { 0: t('lang5') })}
            type="text"
            fullWidth
            name="typeName05"
            value={editType.typeName05}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: {
                maxLength: MaxLength.ManageInspectionItem.TYPE_NAME,
              },
            }}
          />
          <TextField
            margin="dense"
            label={t('LBL_TYPE_NAME_LANG', { 0: t('lang6') })}
            type="text"
            fullWidth
            name="typeName06"
            value={editType.typeName06}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: {
                maxLength: MaxLength.ManageInspectionItem.TYPE_NAME,
              },
            }}
          />
          <TextField
            margin="dense"
            label={t('LBL_TYPE_NAME_LANG', { 0: t('lang7') })}
            type="text"
            fullWidth
            name="typeName07"
            value={editType.typeName07}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: {
                maxLength: MaxLength.ManageInspectionItem.TYPE_NAME,
              },
            }}
          />
          <TextField
            margin="dense"
            label={t('LBL_TYPE_NAME_LANG', { 0: t('lang8') })}
            type="text"
            fullWidth
            name="typeName08"
            value={editType.typeName08}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: {
                maxLength: MaxLength.ManageInspectionItem.TYPE_NAME,
              },
            }}
          />
          <TextField
            margin="dense"
            label={t('LBL_TYPE_NAME_LANG', { 0: t('lang9') })}
            type="text"
            fullWidth
            name="typeName09"
            value={editType.typeName09}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: {
                maxLength: MaxLength.ManageInspectionItem.TYPE_NAME,
              },
            }}
          />
          <TextField
            margin="dense"
            label={t('LBL_TYPE_NAME_LANG', { 0: t('lang10') })}
            type="text"
            fullWidth
            name="typeName10"
            value={editType.typeName10}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: {
                maxLength: MaxLength.ManageInspectionItem.TYPE_NAME,
              },
            }}
          />
          <TextField
            margin="dense"
            label={t('LBL_MANAGE_INSPECTION_MEMO')}
            type="text"
            fullWidth
            name="memo"
            value={editType.memo}
            onChange={handleInputChange}
            slotProps={{
              htmlInput: {
                maxLength: MaxLength.ManageInspectionItem.MEMO,
              },
            }}
          />
          {isAdmin && (
            <Box sx={{ ml: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editType.isOptionalAddition}
                    onChange={(e) => {
                      handleInputChange({
                        target: {
                          name: 'isOptionalAddition',
                          value: e ? e.target.checked : '',
                        },
                      } as React.ChangeEvent<HTMLInputElement>);
                    }}
                  />
                }
                label={t('LBL_MANAGE_INSPECTION_OPTIONAL_ADDITION')}
              />
            </Box>
          )}
          <Box sx={{ ml: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editType.isVisualInspection}
                  onChange={(e) => {
                    handleInputChange({
                      target: {
                        name: 'isVisualInspection',
                        value: e ? e.target.checked : '',
                      },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                />
              }
              label={t('LBL_VISUAL_INSPECTION')}
            />
          </Box>
        </Box>
        {/* マニュアル */}
        <Box sx={{ my: 1, ml: 1 }}>
          <Typography variant="subtitle1" sx={{ my: 1 }}>
            {t('LBL_MANUAL')}
          </Typography>
          <Grid container spacing={2} sx={{ my: 1 }} alignItems={'flex-end'}>
            {manualInfo.fileName ? (
              <Grid
                key={`manual`}
                size={6}
                display="flex"
                flexDirection="column"
                component={Paper}
                sx={{
                  p: '4px',
                  borderRadius: '4px',
                  justifyContent: 'center',
                  '&:hover .hover_delete_icon': {
                    display: 'inline-block',
                  },
                }}
              >
                <DisplayManual
                  manualInfo={manualInfo}
                  handleChange={handleManualUpload}
                />
              </Grid>
            ) : (
              <Grid
                key={`manual_new`}
                size={6}
                display="flex"
                flexDirection="column"
                component={Paper}
                sx={{
                  p: '4px',
                  borderRadius: '4px',
                  justifyContent: 'center',
                }}
              >
                <UploadTypeFile
                  handleAdd={handleManualUpload}
                  isImage={false}
                />
              </Grid>
            )}
            <Grid size={6}>
              <TextField
                sx={{ width: '150px', ml: 1 }}
                margin="dense"
                label={t('LBL_DOCUMENT_PATH_PAGE')}
                type="number"
                fullWidth
                name="page"
                value={manualInfo.page}
                onChange={handleInputChange}
                slotProps={{
                  htmlInput: {
                    min: VALID_MANUAL_PAGE_RANGE.MIN,
                    max: VALID_MANUAL_PAGE_RANGE.MAX,
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
        {/* NGサンプル */}
        <Box sx={{ mt: '1rem', ml: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t('LBL_NG_SAMPLE')}
          </Typography>
          <Grid container spacing={2} size={12}>
            {0 < ngSamples.length ? (
              <>
                {[...ngSamples]
                  .sort((a, b) => a.sort - b.sort)
                  .filter((ng) => !ng.isDeleted)
                  .map((item, i) => {
                    return (
                      <Grid
                        key={`ng_sample_${i}`}
                        size={4}
                        display="flex"
                        flexDirection="column"
                        component={Paper}
                        sx={{
                          p: '4px',
                          borderRadius: '4px',
                          justifyContent: 'center',
                          '&:hover .hover_delete_icon': {
                            display: 'inline-block',
                          },
                        }}
                      >
                        <DisplayNgSample
                          ngSample={item}
                          deleteNgSample={deleteNgSample}
                          handleInputChange={handleNgLabelInputChange}
                          handleImageChange={handleNgImageChange}
                          getFileName={getFileNameFromPath}
                        />
                      </Grid>
                    );
                  })}
                {ngSamples.filter((ng) => !ng.isDeleted).length <
                  ACCEPT_MAX_NG_SAMPLE && (
                  <Grid
                    key={`ng_sample_new`}
                    size={4}
                    display="flex"
                    flexDirection="column"
                    component={Paper}
                    sx={{
                      p: '4px',
                      borderRadius: '4px',
                      justifyContent: 'center',
                    }}
                  >
                    <UploadTypeFile
                      handleAdd={handleNgImageAdd}
                      isImage={true}
                    />
                  </Grid>
                )}
              </>
            ) : (
              <Grid
                key={`ng_sample_new`}
                size={4}
                display="flex"
                flexDirection="column"
                component={Paper}
                sx={{
                  p: '4px',
                  borderRadius: '4px',
                  justifyContent: 'center',
                }}
              >
                <UploadTypeFile handleAdd={handleNgImageAdd} isImage={true} />
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => closeModal(true)}
          color="inherit"
          variant="contained"
          sx={{ width: '120px', mr: 2, my: 1 }}
        >
          {t('BTN_CANCEL')}
        </Button>
        <Button
          onClick={onClickSave}
          color="primary"
          variant="contained"
          sx={{ width: '120px', mr: 3, my: 1 }}
          disabled={isDisabledSaveBtn}
        >
          {t('BTN_SAVE')}
        </Button>
      </DialogActions>
      <Backdrop
        sx={{
          ...LOADING_BACKDROP_STYLE,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {error && <ErrorModal message={error} onClose={hideError} />}
    </Dialog>
  );
};

export default EditTypeModal;
